import { useState } from 'react';
import { ArrowDown, ArrowUp, Info, Plus, Trash2 } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonMint, BoutonSecondaire, MarqueObligatoire, MessageErreur } from '../ui/Primitives';
import type { BlocAffiche, ContenuSaisi, Programme, VersionProgramme } from '../../lib/useProgrammes';

/** Niveaux proposés à la saisie, repris des libellés du référentiel formations. */
const NIVEAUX_COURANTS = [
  'TP Niveau 3 (CAP)',
  'TP Niveau IV (BAC)',
  'TP Niveau V (BTS)',
  'TP Niveau 6 (Licence)',
  'TP Niveau 7 (Master)',
];

interface BlocEnSaisie {
  /** Clé de liste stable, y compris pour un bloc pas encore enregistré. */
  cle: string;
  id?: string;
  libelle: string;
  heures: string;
  /** Ce qui empêche de retirer ce bloc, lu de l'API. */
  refusRetrait: string | null;
}

interface Saisie {
  code: string;
  titre: string;
  niveau: string;
  heures: string;
  heuresEcrits: string;
  heuresEcf: string;
  codeDiplome: string;
  rncp: string;
  blocs: BlocEnSaisie[];
}

let compteurCles = 0;
function nouvelleCle(): string {
  compteurCles += 1;
  return `bloc-${compteurCles}`;
}

function versSaisie(code: string, version: VersionProgramme | null): Saisie {
  if (!version) {
    return {
      code,
      titre: '',
      niveau: '',
      heures: '',
      heuresEcrits: '',
      heuresEcf: '',
      codeDiplome: '',
      rncp: '',
      blocs: [],
    };
  }
  return {
    code,
    titre: version.titre,
    niveau: version.niveau,
    heures: String(version.heures),
    heuresEcrits: String(version.heuresEcrits),
    heuresEcf: String(version.heuresEcf),
    codeDiplome: version.codeDiplome,
    rncp: version.rncp,
    blocs: version.blocs.map((bloc: BlocAffiche) => ({
      cle: bloc.id ?? nouvelleCle(),
      ...(bloc.id ? { id: bloc.id } : {}),
      libelle: bloc.libelle,
      heures: String(bloc.heures),
      refusRetrait: bloc.refusRetrait,
    })),
  };
}

function entier(valeur: string): number {
  const lu = Number(valeur);
  return Number.isFinite(lu) && lu > 0 ? Math.trunc(lu) : 0;
}

const CHAMP =
  'w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm text-codialis-navy placeholder:text-gray-400 focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20 transition min-h-[40px] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-codialis-gray';

/**
 * Saisie d'un programme : nouveau (brouillon), brouillon à reprendre, ou
 * révision d'un programme validé. L'écran envoie le programme entier ; c'est
 * l'API qui dit ensuite ce qui empêche de le valider.
 */
export function ModaleProgramme({
  programme,
  niveauxConnus,
  occupe,
  erreur,
  onFermer,
  onEnregistrer,
}: {
  /** `null` : nouveau programme. */
  programme: Programme | null;
  niveauxConnus: string[];
  occupe: boolean;
  erreur: string | null;
  onFermer: () => void;
  onEnregistrer: (contenu: ContenuSaisi & { code?: string }) => void;
}): JSX.Element {
  const brouillon = programme === null || programme.statut === 'BROUILLON';
  const [saisie, setSaisie] = useState<Saisie>(() =>
    versSaisie(programme?.code ?? '', programme ? (programme.enEdition ?? programme.valide) : null),
  );

  const modifier = (champs: Partial<Saisie>): void => setSaisie((courante) => ({ ...courante, ...champs }));
  const modifierBloc = (index: number, champs: Partial<BlocEnSaisie>): void =>
    modifier({ blocs: saisie.blocs.map((bloc, rang) => (rang === index ? { ...bloc, ...champs } : bloc)) });
  const deplacerBloc = (index: number, sens: -1 | 1): void => {
    const cible = index + sens;
    if (cible < 0 || cible >= saisie.blocs.length) return;
    const blocs = [...saisie.blocs];
    [blocs[index], blocs[cible]] = [blocs[cible]!, blocs[index]!];
    modifier({ blocs });
  };

  const reparties =
    saisie.blocs.reduce((somme, bloc) => somme + entier(bloc.heures), 0) +
    entier(saisie.heuresEcrits) +
    entier(saisie.heuresEcf);
  const annonce = entier(saisie.heures);

  const manques = [
    brouillon && !/^[A-Z0-9-]{2,12}$/.test(saisie.code.trim().toUpperCase()) &&
      'un code de 2 à 12 caractères (majuscules, chiffres, tiret)',
    saisie.titre.trim().length < 3 && 'l’intitulé (3 caractères au moins)',
    saisie.niveau.trim().length < 2 && 'le niveau',
    saisie.codeDiplome.trim().length === 0 && 'le code diplôme',
    saisie.rncp.trim().length === 0 && 'le numéro RNCP',
    annonce < 1 && 'le volume annoncé',
    saisie.blocs.some((bloc) => bloc.libelle.trim().length < 3) && 'l’intitulé de chaque bloc (3 caractères au moins)',
  ].filter((manque): manque is string => typeof manque === 'string');

  const enregistrer = (): void => {
    onEnregistrer({
      ...(brouillon ? { code: saisie.code.trim().toUpperCase() } : {}),
      titre: saisie.titre.trim(),
      niveau: saisie.niveau.trim(),
      heures: annonce,
      heuresEcrits: entier(saisie.heuresEcrits),
      heuresEcf: entier(saisie.heuresEcf),
      codeDiplome: saisie.codeDiplome.trim().toUpperCase(),
      rncp: saisie.rncp.trim(),
      blocs: saisie.blocs.map((bloc) => ({
        ...(bloc.id ? { id: bloc.id } : {}),
        libelle: bloc.libelle.trim(),
        heures: entier(bloc.heures),
      })),
    });
  };

  const titre =
    programme === null
      ? 'Nouveau programme'
      : brouillon
        ? `Brouillon · ${programme.code}`
        : `Révision du programme · ${programme.code}`;
  const sousTitre =
    programme === null
      ? 'Enregistré en brouillon : il n’est proposé nulle part avant sa validation.'
      : brouillon
        ? 'Le brouillon reste invisible des dossiers et des sessions jusqu’à sa validation.'
        : 'La version validée reste en usage jusqu’à la validation de cette révision.';

  return (
    <Modale titre={titre} sousTitre={sousTitre} ouverte onFermer={onFermer} largeur="max-w-3xl">
      <div className="space-y-6 px-4 py-5 sm:px-6">
        <section className="space-y-3.5" aria-labelledby="programme-certification">
          <h3
            id="programme-certification"
            className="border-b border-gray-100 pb-1.5 text-xs font-bold uppercase tracking-wider text-codialis-gray"
          >
            Intitulé, certification et codes
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Champ libelle="Intitulé de la formation" obligatoire>
              <input
                type="text"
                value={saisie.titre}
                onChange={(evenement) => modifier({ titre: evenement.target.value })}
                placeholder="Ex. Manager d’établissement marchand"
                className={CHAMP}
              />
            </Champ>
            <Champ
              libelle="Code"
              obligatoire={brouillon}
              aide={
                brouillon
                  ? '2 à 12 caractères (ex. MEM). Figé à la validation.'
                  : 'Figé : il désigne les dossiers, contrats, séances et sessions.'
              }
            >
              <input
                type="text"
                value={saisie.code}
                onChange={(evenement) => modifier({ code: evenement.target.value.toUpperCase() })}
                disabled={!brouillon}
                placeholder="Ex. MEM"
                className={`${CHAMP} font-mono uppercase`}
              />
            </Champ>
            <Champ libelle="Niveau de la certification visée" obligatoire aide="Le diplôme entre parenthèses fait foi pour le BPF.">
              <input
                type="text"
                list="niveaux-programme"
                value={saisie.niveau}
                onChange={(evenement) => modifier({ niveau: evenement.target.value })}
                placeholder="Ex. TP Niveau V (BTS)"
                className={CHAMP}
              />
              <datalist id="niveaux-programme">
                {[...new Set([...niveauxConnus, ...NIVEAUX_COURANTS])].map((niveau) => (
                  <option key={niveau} value={niveau} />
                ))}
              </datalist>
            </Champ>
            <div className="grid grid-cols-2 gap-3">
              <Champ libelle="Code diplôme" obligatoire>
                <input
                  type="text"
                  value={saisie.codeDiplome}
                  onChange={(evenement) => modifier({ codeDiplome: evenement.target.value.toUpperCase() })}
                  placeholder="Ex. 36T31201"
                  className={`${CHAMP} font-mono uppercase`}
                />
              </Champ>
              <Champ libelle="Numéro RNCP" obligatoire>
                <input
                  type="text"
                  value={saisie.rncp}
                  onChange={(evenement) => modifier({ rncp: evenement.target.value })}
                  placeholder="Ex. 41853"
                  className={`${CHAMP} font-mono`}
                />
              </Champ>
            </div>
          </div>
        </section>

        <section className="space-y-3.5" aria-labelledby="programme-volumes">
          <h3
            id="programme-volumes"
            className="border-b border-gray-100 pb-1.5 text-xs font-bold uppercase tracking-wider text-codialis-gray"
          >
            Volumes
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Champ libelle="Volume annoncé (h)" obligatoire aide="Celui du référentiel de la certification.">
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={5000}
                value={saisie.heures}
                onChange={(evenement) => modifier({ heures: evenement.target.value })}
                className={CHAMP}
              />
            </Champ>
            <Champ libelle="Écrits (h)">
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={5000}
                value={saisie.heuresEcrits}
                onChange={(evenement) => modifier({ heuresEcrits: evenement.target.value })}
                placeholder="0"
                className={CHAMP}
              />
            </Champ>
            <Champ libelle="ECF (h)">
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={5000}
                value={saisie.heuresEcf}
                onChange={(evenement) => modifier({ heuresEcf: evenement.target.value })}
                placeholder="0"
                className={CHAMP}
              />
            </Champ>
          </div>
          <p
            aria-live="polite"
            className={`rounded-xl px-3.5 py-2.5 text-sm ${
              reparties === annonce ? 'bg-codialis-sand text-codialis-navy' : 'bg-amber-50 text-amber-800'
            }`}
          >
            Blocs + écrits + ECF : <strong>{reparties} h</strong> pour <strong>{annonce} h</strong> annoncées
            {reparties === annonce ? '.' : ' : le programme ne se validera pas tant que les deux ne coïncident pas.'}
          </p>
        </section>

        <section className="space-y-3.5" aria-labelledby="programme-blocs">
          <h3
            id="programme-blocs"
            className="border-b border-gray-100 pb-1.5 text-xs font-bold uppercase tracking-wider text-codialis-gray"
          >
            Blocs de compétences
          </h3>
          {saisie.blocs.length === 0 && (
            <p className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 p-4 text-center text-xs text-codialis-gray">
              Aucun bloc pour l’instant. Il en faut au moins un pour valider le programme.
            </p>
          )}
          <ol className="space-y-3">
            {saisie.blocs.map((bloc, index) => (
              <li key={bloc.cle} className="rounded-xl border border-gray-200 bg-white p-3.5">
                <div className="flex flex-col gap-3 md:flex-row md:items-end">
                  <label className="min-w-0 flex-1">
                    <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                      Bloc {index + 1}
                      <MarqueObligatoire />
                    </span>
                    <textarea
                      value={bloc.libelle}
                      onChange={(evenement) => modifierBloc(index, { libelle: evenement.target.value })}
                      rows={2}
                      placeholder="Intitulé du bloc de compétences…"
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-codialis-navy transition focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20"
                    />
                  </label>
                  <label className="w-full md:w-28">
                    <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                      Heures
                    </span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={5000}
                      value={bloc.heures}
                      onChange={(evenement) => modifierBloc(index, { heures: evenement.target.value })}
                      className={CHAMP}
                    />
                  </label>
                  <div className="flex items-center gap-1.5 self-end">
                    <button
                      type="button"
                      onClick={() => deplacerBloc(index, -1)}
                      disabled={index === 0}
                      aria-label={`Monter le bloc ${index + 1}`}
                      className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-codialis-navy transition hover:bg-gray-50 disabled:opacity-30"
                    >
                      <ArrowUp className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deplacerBloc(index, 1)}
                      disabled={index === saisie.blocs.length - 1}
                      aria-label={`Descendre le bloc ${index + 1}`}
                      className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-codialis-navy transition hover:bg-gray-50 disabled:opacity-30"
                    >
                      <ArrowDown className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => modifier({ blocs: saisie.blocs.filter((_bloc, rang) => rang !== index) })}
                      disabled={bloc.refusRetrait !== null}
                      title={bloc.refusRetrait ?? undefined}
                      aria-label={`Retirer le bloc ${index + 1}`}
                      className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-red-600 transition hover:border-red-200 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                {bloc.refusRetrait && (
                  <p className="mt-2 text-[11px] text-codialis-gray">{bloc.refusRetrait}</p>
                )}
              </li>
            ))}
          </ol>
          <BoutonSecondaire
            onClick={() =>
              modifier({
                blocs: [...saisie.blocs, { cle: nouvelleCle(), libelle: '', heures: '', refusRetrait: null }],
              })
            }
          >
            <span className="inline-flex items-center gap-2">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Ajouter un bloc
            </span>
          </BoutonSecondaire>
          <p className="flex items-start gap-2 rounded-xl bg-codialis-sand p-3 text-xs text-codialis-navy">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-codialis-gray" aria-hidden="true" />
            <span>
              Les blocs alimentent le programme imprimé, la convention, les ECF, les feuilles d’émargement et les
              cours en ligne. Un bloc qui porte des devoirs, des ECF, des séances ou des cours se renomme, il ne se
              retire pas.
            </span>
          </p>
        </section>

        {manques.length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <p className="mb-1 font-semibold">À renseigner avant d’enregistrer :</p>
            <ul className="list-inside list-disc space-y-0.5">
              {manques.map((manque) => (
                <li key={manque}>{manque}</li>
              ))}
            </ul>
          </div>
        )}
        {erreur && <MessageErreur message={erreur} />}

        <div className="flex flex-wrap justify-end gap-3 border-t border-gray-100 pt-4">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonMint onClick={enregistrer} disabled={occupe || manques.length > 0}>
            {programme === null
              ? 'Créer le brouillon'
              : brouillon
                ? 'Enregistrer le brouillon'
                : 'Enregistrer la révision'}
          </BoutonMint>
        </div>
      </div>
    </Modale>
  );
}

function Champ({
  libelle,
  obligatoire = false,
  aide,
  children,
}: {
  libelle: string;
  obligatoire?: boolean;
  aide?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-codialis-navy">
        {libelle}
        {obligatoire && <MarqueObligatoire />}
      </span>
      {children}
      {aide && <span className="mt-1 block text-[11px] text-codialis-gray">{aide}</span>}
    </label>
  );
}
